const SPAWN_NAME_COLLATOR = new Intl.Collator(undefined, { numeric: true })
const emptyObject = () => Object.freeze(Object.create(null))

const EMPTY_SPAWN_INDEX = Object.freeze({
  rowsByMap: emptyObject(),
  rowsByMapListKey: emptyObject(),
  rowsByDexID: emptyObject(),
  maps: Object.freeze([])
})

const mapListKey = value => String(value || '').toLowerCase().trim()

const isWaterSpawn = spawn => {
  const area = String(spawn && spawn.area ? spawn.area : '').toLowerCase()
  return area === 'surf' || area === 'water'
}

const freezeRecord = row => {
  const copy = Object.assign({}, row || {})
  Object.keys(copy).forEach(key => {
    if (Array.isArray(copy[key])) {
      copy[key] = Object.freeze(copy[key].slice())
    }
  })
  return Object.freeze(copy)
}

const freezeRecords = rows => Object.freeze((Array.isArray(rows) ? rows : []).map(freezeRecord))

const freezeBucketMap = buckets => {
  Object.keys(buckets).forEach(key => {
    buckets[key] = Object.freeze(buckets[key])
  })
  return Object.freeze(buckets)
}

const buildSpawnIndex = rows => {
  if (!rows || !rows.length) return EMPTY_SPAWN_INDEX

  const rowsByMap = Object.create(null)
  const rowsByMapListKey = Object.create(null)
  const rowsByDexID = Object.create(null)
  const summariesByListKey = Object.create(null)

  rows.forEach(spawn => {
    const mapName = spawn.map || 'Unknown'
    const listKey = mapListKey(mapName)
    const dexID = Number(spawn.dexID || 0)

    if (!rowsByMap[mapName]) rowsByMap[mapName] = []
    rowsByMap[mapName].push(spawn)

    if (listKey) {
      if (!rowsByMapListKey[listKey]) rowsByMapListKey[listKey] = []
      rowsByMapListKey[listKey].push(spawn)

      if (!summariesByListKey[listKey]) {
        summariesByListKey[listKey] = {
          name: mapName,
          searchName: listKey,
          land: 0,
          water: 0,
          total: 0
        }
      }
      if (isWaterSpawn(spawn)) {
        summariesByListKey[listKey].water++
      } else {
        summariesByListKey[listKey].land++
      }
      summariesByListKey[listKey].total++
    }

    if (dexID) {
      if (!rowsByDexID[dexID]) rowsByDexID[dexID] = []
      rowsByDexID[dexID].push(spawn)
    }
  })

  const maps = Object.keys(summariesByListKey)
    .map(key => Object.freeze(Object.assign({}, summariesByListKey[key])))
    .sort((a, b) => SPAWN_NAME_COLLATOR.compare(a.name, b.name))

  return Object.freeze({
    rowsByMap: freezeBucketMap(rowsByMap),
    rowsByMapListKey: freezeBucketMap(rowsByMapListKey),
    rowsByDexID: freezeBucketMap(rowsByDexID),
    maps: Object.freeze(maps)
  })
}

const state = {
  canEditReferenceData: false,
  canAddLegendary: false,
  referenceLookups: {},
  moves: [],
  referenceData: {},
  pokemonCatalog: [],
  rows: [],
  lastDraftSave: null,
  workflowBatches: [],
  workflowChangelog: [],
  canUploadReady: false,
  rowIndex: EMPTY_SPAWN_INDEX,
  usageRanks: [],
  referenceAnalysisLoaded: false,
  referenceDataUpdatedAt: null,
  workflowUpdatedAt: null,
  updatedAt: null
}

const getters = {
  spawnCanEditReferenceData: state => state.canEditReferenceData,
  spawnCanAddLegendary: state => state.canAddLegendary,
  spawnLiveMaps: state => state.rowIndex.maps,
  spawnLiveRowsByDexID: state => state.rowIndex.rowsByDexID,
  spawnLiveRowsByMap: state => state.rowIndex.rowsByMap,
  spawnLiveRowsByMapListKey: state => state.rowIndex.rowsByMapListKey,
  spawnReferenceLookups: state => state.referenceLookups,
  spawnMoves: state => state.moves,
  spawnPokemonCatalog: state => state.pokemonCatalog,
  spawnReferenceAnalysisLoaded: state => state.referenceAnalysisLoaded,
  spawnReferenceData: state => state.referenceData,
  spawnReferenceDataUpdatedAt: state => state.referenceDataUpdatedAt,
  spawnLastDraftSave: state => state.lastDraftSave,
  spawnWorkflowBatches: state => state.workflowBatches,
  spawnWorkflowChangelog: state => state.workflowChangelog,
  spawnWorkflowCanUploadReady: state => state.canUploadReady,
  spawnWorkflowUpdatedAt: state => state.workflowUpdatedAt,
  spawnUsageRanks: state => state.usageRanks,
  spawns: state => state.rows,
  spawnsUpdatedAt: state => state.updatedAt
}

const actions = {
  getSpawns ({ commit }, payload) {
    commit('setSpawns', payload && payload.data ? payload.data : [])
  },
  getSpawnReferenceData ({ commit }, payload) {
    commit('setSpawnReferenceData', payload && payload.data ? payload.data : {})
  },
  getSpawnEditorWorkflow ({ commit }, payload) {
    commit('setSpawnEditorWorkflow', payload && payload.data ? payload.data : {})
  },
  spawnDraftSaved ({ commit }, payload) {
    commit('setSpawnDraftSaved', payload && payload.data ? payload.data : {})
  }
}

const mutations = {
  setSpawnReferenceData (state, payload) {
    state.referenceData = payload.referenceData || {}
    state.referenceLookups = payload.lookups || {}
    if (Object.prototype.hasOwnProperty.call(payload, 'moves')) {
      state.moves = freezeRecords(payload.moves)
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'usageRanks')) {
      state.usageRanks = freezeRecords(payload.usageRanks)
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'pokemonCatalog')) {
      state.pokemonCatalog = freezeRecords(payload.pokemonCatalog)
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'moves') ||
      Object.prototype.hasOwnProperty.call(payload, 'usageRanks') ||
      Object.prototype.hasOwnProperty.call(payload, 'pokemonCatalog')) {
      state.referenceAnalysisLoaded = true
    }
    state.canEditReferenceData = !!payload.canEdit
    state.canAddLegendary = !!payload.canAddLegendary
    state.referenceDataUpdatedAt = new Date().toISOString()
  },
  setSpawnEditorWorkflow (state, payload) {
    state.workflowBatches = Array.isArray(payload.batches) ? payload.batches : []
    state.workflowChangelog = Array.isArray(payload.changelog) ? payload.changelog : []
    state.canUploadReady = !!payload.canUploadReady
    state.workflowUpdatedAt = new Date().toISOString()
  },
  setSpawnDraftSaved (state, payload) {
    state.lastDraftSave = Object.assign({}, payload || {}, {
      receivedAt: new Date().toISOString()
    })
  },
  setSpawns (state, rows) {
    const spawnRows = freezeRecords(rows)
    state.rows = spawnRows
    state.rowIndex = buildSpawnIndex(spawnRows)
    state.updatedAt = new Date().toISOString()
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
